import React, { useEffect, useRef, useState } from 'react';
import { GoogleMap, LoadScriptNext } from '@react-google-maps/api';
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from '@googlemaps/markerclusterer';

import {
  DashboardMapProps,
  DEFAULT_MARKER_COLOR,
  isProperCoordinates,
  markerTooltip,
} from './mapShared';
import { buildClusterElement, summariseCluster } from './clusterPieIcon';

/**
 * The Google Maps dashboard map layer.
 *
 * SW2-2247: moved out of Maps.tsx unchanged, apart from taking its inputs as props and
 * building its cluster icon from the shared builder. **Do not delete this when Mapbox is
 * live** — it is the thing `REACT_APP_MAP_PROVIDER=google` switches back to once the
 * restricted Google Cloud account is reinstated.
 *
 * Requires `REACT_APP_GOOGLE_MAPS_API_KEY` and `REACT_APP_GOOGLE_MAPS_MAP_ID`; the
 * `mapId` is what enables Advanced Markers, which the pin and cluster code below needs.
 */

const MAPS_LIBRARIES: 'marker'[] = ['marker'];

/** Matches the clustering the Mapbox layer configures, so the two behave alike. */
const CLUSTER_MAX_ZOOM = 14;
const CLUSTER_RADIUS = 40;

const DashboardMapGoogle = ({
  mapData,
  filterWiseHexColor,
  currentKey,
  center,
  onSelectProject,
}: DashboardMapProps) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    if (!mapRef.current) return;

    const markers = mapData
      .filter(item =>
        isProperCoordinates(Number(item.latitude), Number(item.longitude)),
      )
      .map(item => {
        const color = filterWiseHexColor[item[currentKey]] || DEFAULT_MARKER_COLOR;
        const pin = new google.maps.marker.PinElement({
          background: color,
          borderColor: color,
          glyphColor: '#ffffff',
        });
        const marker = new google.maps.marker.AdvancedMarkerElement({
          position: { lat: Number(item.latitude), lng: Number(item.longitude) },
          title: markerTooltip(item),
          content: pin,
        });

        if (marker.element) {
          (marker.element as HTMLElement).style.cursor = 'pointer';
        }

        // Stashed on the marker so the cluster renderer can read back the colour and
        // label of everything inside a cluster.
        (marker as any)._color = color;
        (marker as any)._label = item[currentKey] || 'Unknown';
        marker.addListener('click', () => onSelectProject(item));

        return marker;
      });

    const clusterer = new MarkerClusterer({
      map: mapRef.current,
      markers,
      algorithm: new SuperClusterAlgorithm({
        maxZoom: CLUSTER_MAX_ZOOM,
        radius: CLUSTER_RADIUS,
      }),
      renderer: {
        render(cluster) {
          const clusterMarkers = cluster.markers ?? [];
          const summary = summariseCluster(
            clusterMarkers.map(m => ({
              color: (m as any)._color,
              label: (m as any)._label,
            })),
          );

          return new google.maps.marker.AdvancedMarkerElement({
            position: cluster.bounds?.getCenter(),
            content: buildClusterElement(summary),
            zIndex: Math.round(1000 / summary.total),
          });
        },
      },
    });

    return () => {
      clusterer.clearMarkers();
    };
  }, [mapData, filterWiseHexColor, currentKey, mapReady, onSelectProject]);

  return (
    <LoadScriptNext
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
      libraries={MAPS_LIBRARIES}
    >
      <GoogleMap
        mapContainerStyle={{ height: '100%', width: '100%' }}
        center={{ lat: center.lat, lng: center.lng }}
        options={{
          disableDefaultUI: true,
          zoomControl: false,
          fullscreenControl: false,
          streetViewControl: false,
          mapTypeControl: true,
          gestureHandling: 'greedy',
          restriction: {
            latLngBounds: {
              north: 85,
              south: -85,
              east: 180,
              west: -180,
            },
            strictBounds: true,
          },
          maxZoom: 18,
          minZoom: 3,
          mapId: process.env.REACT_APP_GOOGLE_MAPS_MAP_ID,
        }}
        zoom={6}
        onLoad={map => {
          mapRef.current = map;
          setMapReady(true);
        }}
      ></GoogleMap>
    </LoadScriptNext>
  );
};

export default DashboardMapGoogle;
